<script setup lang="ts">
import BaseLoader from '@/components/BaseComponents/BaseLoader.vue';
import { useFetch } from '@/composables/useFetch';
import { usePlatform } from '@/composables/usePlatform';
import { useEnvStore } from '@/stores/envStore';
import { useUserStore } from '@/stores/useUserStore';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';
import { onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const toast = useToast();
const envStore = useEnvStore();
const userStore = useUserStore();
const { fetchData } = useFetch();
const { setAuthToken, isNative } = usePlatform();

const isValidating = ref(true);
const errorMessage = ref<string | null>(null);
const redirectTo = ref('/profile/subscriptions');
const hasValidated = ref(false); // NEW: Track if we've already validated

onMounted(async () => {
  console.log('[MagicLogin] Component mounted');
  console.log('[MagicLogin] Route query:', route.query);
  console.log('[MagicLogin] isNative:', isNative.value);

  // NEW: Check if already validated to prevent re-validation on back navigation
  const alreadyValidated = sessionStorage.getItem('magic_link_validated');
  if (alreadyValidated === 'true') {
    console.log('[MagicLogin] Token already validated, redirecting to home');
    router.replace({ name: 'home' });
    return;
  }

  const token = route.query.token as string;

  if (!token) {
    console.error('[MagicLogin] No token found in query');
    errorMessage.value = t('auth.magicLink.noToken');
    isValidating.value = false;
    return;
  }

  console.log('[MagicLogin] Token found:', token.substring(0, 20) + '...');

  try {
    console.log('[MagicLogin] Calling API to validate token...');
    const { data, error } = await fetchData(`${envStore.apiUrl}/magic-login`, 'POST', {
      token,
    });

    console.log('[MagicLogin] API Response:', { data, error });

    if (error || !data) {
      console.error('[MagicLogin] Validation failed:', error);
      errorMessage.value = data?.message || error?.details?.message || error?.message || t('auth.magicLink.invalidOrExpired');
      isValidating.value = false;
      return;
    }

    console.log('[MagicLogin] Token validated successfully');
    console.log('[MagicLogin] Received Sanctum token:', data.token?.substring(0, 20) + '...');

    // Mark as validated BEFORE any redirects
    sessionStorage.setItem('magic_link_validated', 'true');
    hasValidated.value = true;

    redirectTo.value = data.redirect_to || '/profile/subscriptions';

    if (isNative.value) {
      console.log('[MagicLogin] Storing token in Capacitor storage (native)');
      await setAuthToken(data.token);
    } else {
      console.log('[MagicLogin] Storing token in localStorage (web)');
      localStorage.setItem('auth_token', data.token);

      const storedToken = localStorage.getItem('auth_token');
      console.log('[MagicLogin] Token stored successfully:', !!storedToken);

      if (!storedToken) {
        console.error('[MagicLogin] Token failed to persist to localStorage');
        errorMessage.value = t('auth.magicLink.tokenStorageFailed');
        isValidating.value = false;
        sessionStorage.removeItem('magic_link_validated'); // Clean up
        return;
      }

      sessionStorage.setItem('magic_link_redirect', redirectTo.value);

      setTimeout(() => {
        console.log('[MagicLogin] Reloading app to apply authentication...');
        window.location.href = redirectTo.value;
      }, 100);

      return;
    }

    // For native
    console.log('[MagicLogin] Initializing user data...');
    await userStore.initUser();
    console.log('[MagicLogin] User initialized successfully:', userStore.user?.email);

    toast.add({
      severity: 'success',
      summary: t('common.success'),
      detail: t('auth.magicLink.success'),
      life: 3000,
    });

    console.log('[MagicLogin] Redirecting to:', redirectTo.value);
    setTimeout(() => {
      router.replace(redirectTo.value); // Use replace instead of push
    }, 500);
  } catch (err) {
    console.error('[MagicLogin] Unexpected error:', err);
    errorMessage.value = t('common.errorOccurred');
    isValidating.value = false;
    sessionStorage.removeItem('magic_link_validated'); // Clean up on error
  }
});

const goToLogin = () => {
  sessionStorage.removeItem('magic_link_validated'); // Clean up
  router.push({ name: 'home' });
};
</script>

<template>
  <main>
    <div class="flex flex-col items-center justify-center space-y-4 p-6">
      <img src="@/assets/images/avatar-rounded.webp" :alt="t('homeView.altImageAvatar')" class="mx-auto w-1/4 max-w-32" />

      <!-- Loading State -->
      <div v-if="isValidating" class="flex flex-col items-center text-center">
        <BaseLoader />
        <p class="mt-4 text-lg">{{ t('auth.magicLink.validating') }}</p>
      </div>

      <!-- Error State -->
      <div v-else-if="errorMessage" class="text-center">
        <i class="pi pi-exclamation-triangle mb-4 text-4xl text-red-500"></i>
        <h2 class="mb-2 font-semibold text-xl">{{ t('auth.magicLink.error') }}</h2>
        <p class="mb-4 text-gray-600">{{ errorMessage }}</p>
        <Button :label="t('auth.magicLink.backToLogin')" @click="goToLogin"></Button>
      </div>
    </div>
  </main>
</template>
